/**
 * TaskBoardList — responsive grid of task board cards.
 * - First board auto-created ("Kunlik vazifalar")
 * - Placeholder "+" card always visible at end
 * - Free: 1 board max. Premium/Expert/Company: unlimited.
 * - New boards created instantly with default name, user renames via menu.
 * Desktop: multi-column grid. Mobile: vertical stack.
 */

import React, { useRef, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Lock } from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { PremiumPage } from '../../profile/components/premium-page'
import {
  useTaskBoards,
  useCreateTaskBoard,
  useUpdateTaskBoard,
  useDeleteTaskBoard,
  useCreateTaskItem,
  useDeleteTaskItem,
  useToggleTaskItem,
  useClearCompletedItems,
} from '@turnifly/shared/hooks'
import { TaskBoard } from './task-board'

const DEFAULT_BOARD_NAME_UZ = 'Kunlik vazifalar'
const DEFAULT_BOARD_NAME_EN = 'Daily tasks'
const NEW_BOARD_NAME_UZ = 'Vazifalar'
const NEW_BOARD_NAME_EN = 'Tasks'
const MAX_BOARDS = 10

interface BoardData {
  id: string
  name: string
  sort_order: number
  items: Array<{
    id: string
    text: string
    completed: boolean
    sort_order: number
  }>
}

function parseBoardData(raw: Array<Record<string, unknown>>, defaultName: string): BoardData[] {
  return raw.map((board) => ({
    id: String(board.id ?? ''),
    name: String(board.name ?? '') || defaultName,
    sort_order: typeof board.sort_order === 'number' ? board.sort_order : 0,
    items: (Array.isArray(board.items) ? board.items : []).map((item: Record<string, unknown>) => ({
      id: String(item.id ?? ''),
      text: String(item.text ?? ''),
      completed: Boolean(item.completed),
      sort_order: typeof item.sort_order === 'number' ? item.sort_order : 0,
    })),
  }))
}

/** Check if user can create boards (premium, expert, or company) */
function canCreateBoards(user: { is_premium?: boolean; badge_type?: string | null } | null): boolean {
  if (!user) return false
  if (user.is_premium) return true
  if (user.badge_type === 'expert' || user.badge_type === 'organization' || user.badge_type === 'company') return true
  return false
}

export const TaskBoardList: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { user } = useAuthStore()
  const userId = user?.id ?? ''
  const hasAccess = canCreateBoards(user)

  const { data, isSuccess } = useTaskBoards(userId || undefined)
  const createBoard = useCreateTaskBoard(userId)
  const updateBoard = useUpdateTaskBoard(userId)
  const deleteBoardMut = useDeleteTaskBoard(userId)
  const createItem = useCreateTaskItem(userId)
  const deleteItem = useDeleteTaskItem(userId)
  const toggleItem = useToggleTaskItem(userId)
  const clearCompleted = useClearCompletedItems(userId)

  const premiumTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autoCreatedRef = useRef(false)

  const defaultName = i18n.language === 'uz' ? DEFAULT_BOARD_NAME_UZ : DEFAULT_BOARD_NAME_EN
  const newBoardName = i18n.language === 'uz' ? NEW_BOARD_NAME_UZ : NEW_BOARD_NAME_EN
  const boards = parseBoardData((data ?? []) as Array<Record<string, unknown>>, defaultName)

  // Auto-create first board if none exist
  useEffect(() => {
    if (isSuccess && boards.length === 0 && userId && !autoCreatedRef.current && !createBoard.isPending) {
      autoCreatedRef.current = true
      createBoard.mutate({ name: defaultName, sortOrder: 0 })
    }
  }, [isSuccess, boards.length, userId, defaultName, createBoard])

  // Cleanup premium hint timeout
  useEffect(() => {
    return (): void => {
      if (premiumTimerRef.current) clearTimeout(premiumTimerRef.current)
    }
  }, [])

  const [showPremiumPage, setShowPremiumPage] = useState(false)

  const handleNewBoardClick = (): void => {
    if (!hasAccess && boards.length >= 1) {
      setShowPremiumPage(true)
      return
    }
    if (boards.length >= MAX_BOARDS) return
    // Instant creation with default name — user renames via menu
    createBoard.mutate({ name: newBoardName, sortOrder: boards.length })
  }

  const handleToggleItem = (itemId: string, completed: boolean): void => {
    toggleItem.mutate({ itemId, completed })
  }

  const handleAddItem = (boardId: string, text: string, sortOrder: number): void => {
    createItem.mutate({ boardId, text, sortOrder })
  }

  const handleClearCompleted = (boardId: string): void => {
    clearCompleted.mutate(boardId)
  }

  const handleDeleteItem = (itemId: string): void => {
    deleteItem.mutate(itemId)
  }

  const handleDeleteBoard = (boardId: string): void => {
    deleteBoardMut.mutate(boardId)
  }

  const handleRenameBoard = (boardId: string, name: string): void => {
    updateBoard.mutate({ boardId, name })
  }

  return (
    <div className="flex flex-col gap-2.5">
      {/* Premium hint removed — PremiumPage opens instead */}

      {/* Board cards grid + placeholder card */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
        {/* Existing boards */}
        {boards.map((board, index) => (
          <TaskBoard
            key={board.id}
            boardId={board.id}
            boardName={board.name}
            items={board.items}
            isDefault={index === 0}
            colorIndex={index}
            onToggleItem={handleToggleItem}
            onAddItem={handleAddItem}
            onClearCompleted={handleClearCompleted}
            onDeleteItem={handleDeleteItem}
            onDeleteBoard={handleDeleteBoard}
            onRenameBoard={handleRenameBoard}
          />
        ))}

        {/* Placeholder "+" card — hidden when max reached */}
        {boards.length < MAX_BOARDS && <button
          onClick={handleNewBoardClick}
          disabled={createBoard.isPending}
          className="rounded-2xl border-2 border-dashed border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/[0.12] transition-all min-h-[200px] flex flex-col items-center justify-center gap-3 tap-highlight-transparent group"
        >
          <div className="w-14 h-14 rounded-full bg-white/[0.04] group-hover:bg-white/[0.08] flex items-center justify-center transition-colors">
            {!hasAccess && boards.length >= 1 ? (
              <Lock size={22} className="text-text-hint" />
            ) : (
              <Plus size={24} className="text-primary" />
            )}
          </div>
          <span className="text-[14px] font-medium text-text-hint group-hover:text-text transition-colors">
            {t('home.createBoard')}
          </span>
          {!hasAccess && boards.length >= 1 && (
            <span className="text-[11px] text-[#F59E0B]">Premium</span>
          )}
        </button>}
      </div>
      <PremiumPage isOpen={showPremiumPage} onClose={() => setShowPremiumPage(false)} />
    </div>
  )
}
